import { useCallback } from 'react'
import log from 'loglevel'
import {
  DeleteRequestFindingRequest,
  ListRequestFindingsRequest,
  ListRequestFindingsResponse,
  RequestFinding,
  UpsertRequestFindingRequest,
} from '@trustero/trustero-api-web/lib/request/request_pb'
import { RequestPromiseClient } from '@trustero/trustero-api-web/lib/request/request_grpc_web_pb'
import { useAuthorizedGrpcClient } from '../../../adapter'
import { useSwrImmutableGrpc } from '../useSwrImmutableGrpc'
import { GrpcResponse, NewGrpcResponse } from '../hooks/types'
import { useGrpcRevalidateByMethod } from '../useGrpcMutate'

export const useInvalidateRequestFindingsCache = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()
  return useCallback(async () => {
    await mutateFunc(RequestPromiseClient.prototype.listRequestFindings)
  }, [mutateFunc])
}

export const useListRequestFindings = (
  requestId: string,
  shouldFetch = true,
): GrpcResponse<ListRequestFindingsResponse> => {
  const req = new ListRequestFindingsRequest().setRequestId(requestId)
  const { response } = useSwrImmutableGrpc(
    RequestPromiseClient.prototype.listRequestFindings,
    req,
    shouldFetch && !!requestId,
  )
  return NewGrpcResponse(response)
}

export const useUpsertRequestFinding = (
  _requestId: string,
): ((finding: RequestFinding) => Promise<RequestFinding | undefined>) => {
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  const invalidate = useInvalidateRequestFindingsCache()

  return useCallback(
    async (finding: RequestFinding) => {
      try {
        const req = new UpsertRequestFindingRequest().setFinding(finding)
        const result = await requestClient.upsertRequestFinding(req)
        await invalidate()
        return result
      } catch (e) {
        log.error('useUpsertRequestFinding: error', e)
      }
    },
    [requestClient, invalidate],
  )
}

export const useDeleteRequestFinding = (
  _requestId: string,
): ((findingId: string) => Promise<void>) => {
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  const invalidate = useInvalidateRequestFindingsCache()

  return useCallback(
    async (findingId: string) => {
      try {
        const req = new DeleteRequestFindingRequest().setFindingId(findingId)
        await requestClient.deleteRequestFinding(req)
        await invalidate()
      } catch (e) {
        log.error('useDeleteRequestFinding: error', e)
      }
    },
    [requestClient, invalidate],
  )
}
